/**
 * Motor de Impresión Cliente Universal para RealPOS
 * Gestiona el almacenamiento local de la impresora por dispositivo,
 * formatos de papel (80mm / 58mm) por Piso/Área, modo de impresión silenciosa (Kiosk Mode vs Confirmación window.print),
 * formateo de comandas de alto impacto y anulación, impresión física via iframe/bluetooth/red IP,
 * y cola de impresión offline con reintento automático.
 */

const PRINT_QUEUE_KEY = 'printQueue';
const PAPER_FORMATS_KEY = 'realpos_paper_formats';
const BT_DEVICE_NAME_KEY = 'realpos_bt_device_name';

let cachedBluetoothDevice = null;

export const getCachedBluetoothDevice = () => cachedBluetoothDevice;

/**
 * Persiste el nombre del dispositivo Bluetooth en localStorage y actualiza la caché en RAM.
 * Llamar siempre que se vincule exitosamente un dispositivo.
 */
export const setCachedBluetoothDevice = (device) => {
  cachedBluetoothDevice = device;
  // Persistir el nombre en localStorage para sobrevivir recargas de página
  if (device && device.name) {
    try {
      localStorage.setItem(BT_DEVICE_NAME_KEY, device.name);
      console.log(`💾 Nombre de dispositivo Bluetooth guardado en localStorage: ${device.name}`);
    } catch (e) {
      console.warn('⚠️ No se pudo guardar el nombre del dispositivo Bluetooth en localStorage:', e);
    }
  }
};

/**
 * Lee el nombre de dispositivo Bluetooth guardado en localStorage (sobrevive recargas).
 */
export const getSavedBluetoothDeviceName = () => {
  try {
    return localStorage.getItem(BT_DEVICE_NAME_KEY) || null;
  } catch (e) {
    return null;
  }
};

/**
 * Elimina el nombre de dispositivo Bluetooth guardado en localStorage.
 */
export const clearSavedBluetoothDeviceName = () => {
  try {
    localStorage.removeItem(BT_DEVICE_NAME_KEY);
  } catch (e) {}
};

/**
 * Restaura automáticamente la vinculación Bluetooth mediante navigator.bluetooth.getDevices().
 * Estrategia multicapa:
 *   1. Caché en RAM (si ya vinculado en esta sesión)
 *   2. navigator.bluetooth.getDevices() con coincidencia por nombre guardado en localStorage
 *   3. Retorna null si ninguna opción da resultado (el caller decidirá si abrir picker)
 */
export const restoreBluetoothDevice = async (impresoraInfo) => {
  // Capa 1: Caché en RAM de la sesión actual
  if (cachedBluetoothDevice && cachedBluetoothDevice.gatt) {
    return cachedBluetoothDevice;
  }
  // Capa 2: Web Bluetooth getDevices() con nombre guardado en localStorage
  if (typeof navigator !== 'undefined' && 'bluetooth' in navigator && typeof navigator.bluetooth?.getDevices === 'function') {
    try {
      const permittedDevices = await navigator.bluetooth.getDevices();
      if (permittedDevices && permittedDevices.length > 0) {
        // Priorizar: nombre del objeto impresora → nombre guardado en localStorage → cualquier dispositivo autorizado
        const targetName = (
          (impresoraInfo?.nombre || impresoraInfo?.ip || '').trim() ||
          getSavedBluetoothDeviceName() ||
          ''
        ).toLowerCase();
        let device = null;
        if (targetName) {
          device = permittedDevices.find(d => d.name && d.name.toLowerCase().includes(targetName));
        }
        if (!device) {
          device = permittedDevices[0];
        }
        if (device) {
          cachedBluetoothDevice = device;
          // Actualizar nombre guardado si difiere
          if (device.name) {
            try { localStorage.setItem(BT_DEVICE_NAME_KEY, device.name); } catch (e) {}
          }
          console.log(`✨ Dispositivo Bluetooth restaurado automáticamente via getDevices(): ${device.name || 'Desconocido'}`);
          return device;
        }
      }
    } catch (e) {
      console.warn('⚠️ Error al consultar navigator.bluetooth.getDevices():', e);
    }
  }
  return null;
};

/**
 * Obtiene el ID del usuario activo desde localStorage ('auth')
 */
export const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem('auth');
    if (!raw) return 'default';
    const authObj = JSON.parse(raw);
    return authObj?.user?.id || authObj?.id || authObj?.userId || 'default';
  } catch (e) {
    return 'default';
  }
};

/**
 * Obtiene la clave dinámica de localStorage para la impresora local por usuario
 */
export const getLocalPrinterKey = (userId) => {
  const uid = userId || getCurrentUserId();
  return 'mi_impresora_local_usr_' + uid;
};

/**
 * Obtiene la clave dinámica de localStorage para el modo silencioso por usuario
 */
export const getSilentPrintKey = (userId) => {
  const uid = userId || getCurrentUserId();
  return 'realpos_silent_print_mode_usr_' + uid;
};

// ─── IMPRESIÓN SILENCIOSA (KIOSK MODE VS CONFIRMACIÓN) ─────────────────────

/**
 * Obtiene el tipo de modo de emisión ('silencioso' | 'confirmacion')
 */
export const getSilentModeType = (userId) => {
  try {
    const key = getSilentPrintKey(userId);
    const val = localStorage.getItem(key);
    if (val === 'confirmacion' || val === 'false') {
      return 'confirmacion';
    }
    return 'silencioso';
  } catch (e) {
    console.error('Error al obtener tipo de modo silencioso:', e);
    return 'silencioso';
  }
};

/**
 * Obtiene el estado booleano del modo de impresión silenciosa (true = Instantánea Kiosk / Background, false = Cuadro de Confirmación window.print)
 */
export const getSilentMode = (userId) => {
  return getSilentModeType(userId) === 'silencioso';
};

/**
 * Establece el modo de impresión silenciosa ('silencioso' | 'confirmacion' o boolean)
 */
export const setSilentMode = (enabledOrMode, userId) => {
  try {
    const modeValue = (enabledOrMode === 'confirmacion' || enabledOrMode === false || enabledOrMode === 'false')
      ? 'confirmacion'
      : 'silencioso';
    const key = getSilentPrintKey(userId);
    localStorage.setItem(key, modeValue);
    return true;
  } catch (e) {
    console.error('Error al guardar modo silencioso:', e);
    return false;
  }
};

// ─── FORMATOS DE PAPEL POR PISO / ÁREA ──────────────────────────────────────

/**
 * Obtiene la clave dinámica de localStorage para los formatos de papel por usuario
 */
export const getPaperFormatsKey = (userId) => {
  const uid = userId || getCurrentUserId();
  return 'realpos_paper_formats_usr_' + uid;
};

/**
 * Obtiene la configuración de formatos de papel por piso y área
 */
export const getPaperFormats = (userId) => {
  try {
    const key = getPaperFormatsKey(userId);
    const raw = localStorage.getItem(key);
    if (!raw) {
      return 80; // 80mm por defecto
    }
    const val = parseInt(raw, 10);
    return isNaN(val) ? 80 : val;
  } catch (e) {
    console.error('Error al leer formatos de papel:', e);
    return 80;
  }
};

/**
 * Guarda la tabla completa de formatos de papel
 */
export const saveAllPaperFormats = (formatsObj, userId) => {
  try {
    const key = getPaperFormatsKey(userId);
    localStorage.setItem(key, String(formatsObj));
    return true;
  } catch (e) {
    console.error('Error al guardar formatos de papel:', e);
    return false;
  }
};

/**
 * Guarda el formato de papel (80 o 58) para un piso y área específicos
 */
export const setPaperFormat = (piso, area, ancho, userId) => {
  try {
    const key = getPaperFormatsKey(userId);
    localStorage.setItem(key, String(ancho));
    return true;
  } catch (e) {
    console.error('Error al guardar formato de papel:', e);
    return false;
  }
};

/**
 * Obtiene el ancho de papel (80 o 58) asignado a un piso y área
 */
export const getPaperFormatForArea = (piso = 1, area = 'todas', userId) => {
  return getPaperFormats(userId);
};

// ─── ALMACENAMIENTO LOCAL DE IMPRESORA ──────────────────────────────────────

/**
 * Obtiene la impresora asignada a este usuario desde localStorage
 */
export const getLocalPrinter = (userId) => {
  try {
    const key = getLocalPrinterKey(userId);
    const raw = localStorage.getItem(key);
    if (!raw) {
      return {
        tipo_conexion: 'virtual',
        nombre: 'Virtual KDS'
      };
    }
    return JSON.parse(raw);
  } catch (e) {
    console.error('Error al leer mi_impresora_local:', e);
    return {
      tipo_conexion: 'virtual',
      nombre: 'Virtual KDS'
    };
  }
};

/**
 * Guarda la impresora por defecto para este usuario
 */
export const setLocalPrinter = (printerData, userId) => {
  try {
    const key = getLocalPrinterKey(userId);
    localStorage.setItem(key, JSON.stringify(printerData));
    return true;
  } catch (e) {
    console.error('Error al guardar mi_impresora_local:', e);
    return false;
  }
};

/**
 * Elimina la impresora asignada a este usuario
 */
export const clearLocalPrinter = (userId) => {
  try {
    const key = getLocalPrinterKey(userId);
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    console.error('Error al eliminar mi_impresora_local:', e);
    return false;
  }
};

// ─── COLA DE IMPRESIÓN OFFLINE ─────────────────────────────────────────────

/**
 * Obtiene los trabajos de impresión pendientes en la cola offline
 */
export const getPrintQueue = () => {
  try {
    const raw = localStorage.getItem(PRINT_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('Error al leer la cola de impresión:', e);
    return [];
  }
};

/**
 * Añade una comanda a la cola offline
 */
export const addToPrintQueue = (datosComanda, tipoComanda = 'NUEVA') => {
  try {
    const queue = getPrintQueue();
    const item = {
      id: `job_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      datosComanda,
      tipoComanda,
      createdAt: new Date().toISOString(),
      reintentos: 0
    };
    queue.push(item);
    localStorage.setItem(PRINT_QUEUE_KEY, JSON.stringify(queue));
    console.warn('Comanda añadida a la cola offline:', item.id);
    return item;
  } catch (e) {
    console.error('Error al guardar en cola offline:', e);
    return null;
  }
};

/**
 * Elimina un trabajo de la cola offline
 */
export const removeFromPrintQueue = (jobId) => {
  try {
    const queue = getPrintQueue().filter(job => job.id !== jobId);
    localStorage.setItem(PRINT_QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.error('Error al eliminar trabajo de la cola:', e);
  }
};

/**
 * Procesa la cola offline de impresiones guardadas
 */
export const processPrintQueue = async () => {
  const queue = getPrintQueue();
  if (queue.length === 0) return { procesados: 0, pendientes: 0 };

  console.log(`Procesando cola de impresión offline (${queue.length} pendientes)...`);
  let exitoCount = 0;

  for (const job of [...queue]) {
    try {
      const res = await ejecutarImpresionFisica(job.datosComanda, job.tipoComanda, true);
      if (res.success) {
        removeFromPrintQueue(job.id);
        exitoCount++;
      } else {
        job.reintentos = (job.reintentos || 0) + 1;
        const currentQueue = getPrintQueue();
        const idx = currentQueue.findIndex(q => q.id === job.id);
        if (idx !== -1) {
          currentQueue[idx] = job;
          localStorage.setItem(PRINT_QUEUE_KEY, JSON.stringify(currentQueue));
        }
      }
    } catch (err) {
      console.error(`Fallo al reintentar trabajo ${job.id}:`, err);
    }
  }

  return { procesados: exitoCount, pendientes: getPrintQueue().length };
};

// Listener para procesar cola automáticamente al volver la conexión
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('📶 Conexión restablecida. Ejecutando cola de impresión pendiente...');
    processPrintQueue();
  });
}

// ─── FORMATEADOR DE COMANDAS DE ALTO IMPACTO (HTML / TÉRMICA) ───────────────

/**
 * Formateadores de encabezado para evitar duplicación de palabras (ej: MESA MESA 5 o PISO PISO 1)
 */
const formatMesaHeader = (mesaVal, paraLlevar = false) => {
  if (!mesaVal) return paraLlevar ? 'PARA LLEVAR' : 'COMANDA';
  const str = String(mesaVal).trim();
  if (/^(mesa|para llevar|directo)/i.test(str)) return str.toUpperCase();
  return `MESA ${str.toUpperCase()}`;
};

const formatPisoHeader = (pisoVal) => {
  if (pisoVal === undefined || pisoVal === null || pisoVal === '') return '';
  const str = String(pisoVal).trim();
  if (/^piso/i.test(str)) return str.toUpperCase();
  return `PISO ${str.toUpperCase()}`;
};

/**
 * Agrupa los ítems de la comanda por tiempo/categoría
 */
const agruparItemsPorTiempo = (items = []) => {
  const grupos = {};
  items.forEach(item => {
    let rawCat = item.categoria_nombre || item.categoria || item.nombre_categoria || item.tiempo || item.area || 'GENERAL';
    if (typeof rawCat === 'object' && rawCat !== null) rawCat = rawCat.nombre || rawCat.name || 'GENERAL';
    const catUpper = String(rawCat).trim().toUpperCase() || 'GENERAL';
    if (!grupos[catUpper]) grupos[catUpper] = [];
    grupos[catUpper].push(item);
  });
  return grupos;
};

const notesHasContent = (val) => {
  return typeof val === 'string' && val.trim().length > 0;
};

const stringToBytes = (str) => {
  const emojiReplacements = {
    '❌': '[X]', '📍': 'PISO', '⚠️': '[!]', '▶': '>', '➔': '->'
  };
  const cp850Map = {
    'ñ': 0xF1, 'Ñ': 0xD1,
    'á': 0xA0, 'é': 0x82, 'í': 0xA1, 'ó': 0xA2, 'ú': 0xA3,
    'Á': 0xB5, 'É': 0x90, 'Í': 0xD6, 'Ó': 0xE0, 'Ú': 0xE9,
    'ü': 0x81, 'Ü': 0x9A,
    '¿': 0xA8, '¡': 0xAD
  };
  let sanitized = str || '';
  for (const [key, value] of Object.entries(emojiReplacements)) {
    sanitized = sanitized.split(key).join(value);
  }
  const bytes = [];
  for (let i = 0; i < sanitized.length; i++) {
    const char = sanitized[i];
    if (cp850Map[char] !== undefined) {
      bytes.push(cp850Map[char]);
    } else {
      const code = char.charCodeAt(0);
      bytes.push(code < 256 ? code : 63);
    }
  }
  return new Uint8Array(bytes);
};

class EscPosBuilder {
  constructor() {
    this.buffer = [];
  }
  
  add(bytes) {
    if (bytes instanceof Uint8Array || Array.isArray(bytes)) {
      for (let i = 0; i < bytes.length; i++) {
        this.buffer.push(bytes[i]);
      }
    } else {
      this.buffer.push(bytes);
    }
    return this;
  }
  
  init() {
    return this.add([0x1B, 0x40, 0x1B, 0x74, 0x01]);
  }
  
  align(dir) {
    const alignVal = dir === 'center' ? 1 : dir === 'right' ? 2 : 0;
    return this.add([0x1B, 0x61, alignVal]);
  }
  
  bold(on) {
    return this.add([0x1B, 0x45, on ? 1 : 0]);
  }
  
  size(widthVal, heightVal) {
    const n = ((widthVal & 0x07) << 4) | (heightVal & 0x07);
    return this.add([0x1D, 0x21, n]);
  }
  
  text(str) {
    return this.add(stringToBytes(str));
  }
  
  line(str = '') {
    if (str) {
      this.text(str);
    }
    return this.add(0x0A);
  }
  
  feed(lines = 1) {
    for (let i = 0; i < lines; i++) {
      this.add(0x0A);
    }
    return this;
  }
  
  cut() {
    return this.add([0x1D, 0x56, 66, 0x00]);
  }
  
  build() {
    return new Uint8Array(this.buffer);
  }
}

/**
 * Genera el buffer de bytes ESC/POS para Comandas de Impacto
 */
export const generarBytesComandaImpactoESCPOS = (datos, anchoPapel = 80) => {
  const builder = new EscPosBuilder();
  builder.init();
  
  const charCount = parseInt(anchoPapel, 10) === 58 ? 32 : 48;
  const separator = '='.repeat(charCount);
  const dashedSeparator = '-'.repeat(charCount);
  
  const mesaText = formatMesaHeader(datos.mesa, datos.paraLlevar);
  const pisoText = formatPisoHeader(datos.piso);
  const mozoText = datos.mozo || datos.usuario || 'Sistema';
  const fechaText = datos.fecha || new Date().toLocaleDateString('es-PE');
  const horaText = datos.hora || new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
  const numeroOrden = datos.ordenId || datos.id || datos.numeroComanda || 'S/N';
  
  // Título mesa
  builder.align('center').bold(true).size(1, 1);
  builder.line(mesaText);
  
  if (pisoText) {
    builder.size(0, 1).line(`📍 ${pisoText}`);
  }
  
  builder.size(0, 0).bold(false).feed(1);
  
  const lineTwoCols = (left, right) => {
    const spaceCount = charCount - left.length - right.length;
    if (spaceCount <= 0) {
      return left + ' ' + right;
    }
    return left + ' '.repeat(spaceCount) + right;
  };
  
  builder.align('left');
  builder.line(lineTwoCols(`ORDEN: #${numeroOrden}`, `MOZO: ${mozoText}`));
  builder.line(lineTwoCols(`FECHA: ${fechaText}`, `HORA: ${horaText}`));
  builder.line(separator);
  
  // Items por tiempo
  const grupos = agruparItemsPorTiempo(datos.items || []);
  
  Object.keys(grupos).forEach(nombreGrupo => {
    const list = grupos[nombreGrupo];
    if (list.length === 0) return;
    
    builder.feed(1).bold(true).line(`> ${nombreGrupo}`);
    builder.line(dashedSeparator).bold(false);
    
    list.forEach(item => {
      const cant = item.cantidad || item.cant || 1;
      const nombre = (item.nombre || item.producto || 'Producto').toUpperCase();
      const notas = item.notes || item.notas || item.observaciones || item.nota || '';
      
      builder.bold(true).size(0, 1);
      builder.text(`${cant}x `);
      builder.bold(true).size(0, 0);
      builder.line(nombre);
      
      if (notesHasContent(notas)) {
        builder.bold(false).align('left');
        builder.line(`  * ATENCION *`);
        builder.line(`  -> ${notas.toUpperCase()}`);
      }
    });
  });
  
  const notaGeneral = datos.notaGeneral || datos.observacion;
  if (notesHasContent(notaGeneral)) {
    builder.feed(1).align('center').bold(true);
    builder.line(`* NOTA GENERAL *`);
    builder.line(notaGeneral.toUpperCase());
  }
  
  builder.feed(1).align('center').bold(true).line(separator);
  builder.line('=== FIN DE COMANDA ===');
  builder.feed(4).cut();
  
  return builder.build();
};

/**
 * Genera el buffer de bytes ESC/POS para Comandas de Anulación
 */
export const generarBytesComandaAnulacionESCPOS = (datos, anchoPapel = 80) => {
  const builder = new EscPosBuilder();
  builder.init();
  
  const charCount = parseInt(anchoPapel, 10) === 58 ? 32 : 48;
  const separator = '='.repeat(charCount);
  
  const mesaText = formatMesaHeader(datos.mesa, datos.paraLlevar);
  const pisoText = formatPisoHeader(datos.piso);
  const mozoText = datos.mozo || datos.usuario || 'Sistema';
  const horaText = datos.hora || new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
  const motivoAnulacion = (datos.motivo || datos.razon || 'SOLICITUD DE CLIENTE').toUpperCase();
  
  builder.align('center').bold(true).size(0, 1);
  builder.line('*** CANCELACION / ANULACION ***');
  builder.feed(1);
  
  builder.size(1, 1);
  builder.line(mesaText);
  if (pisoText) {
    builder.size(0, 1).line(`📍 ${pisoText}`);
  }
  
  builder.size(0, 0).bold(false).feed(1);
  
  const lineTwoCols = (left, right) => {
    const spaceCount = charCount - left.length - right.length;
    if (spaceCount <= 0) {
      return left + ' ' + right;
    }
    return left + ' '.repeat(spaceCount) + right;
  };
  
  builder.line(lineTwoCols(`HORA: ${horaText}`, `MOZO: ${mozoText}`));
  builder.line(separator);
  
  builder.feed(1).bold(true).line('MOTIVO DE ANULACION:');
  builder.line(motivoAnulacion).bold(false).feed(1);
  
  builder.bold(true).line('ITEMS CANCELADOS:');
  builder.line('-'.repeat(charCount)).bold(false);
  
  const items = datos.items || [];
  items.forEach(item => {
    const cant = item.cantidad || item.cant || 1;
    const nombre = (item.nombre || item.producto || 'Producto').toUpperCase();
    const notas = item.notes || item.notas || item.observaciones || '';
    
    builder.bold(true).size(0, 1);
    builder.text(`[X] -${cant}x `);
    builder.line(nombre);
    builder.bold(false).size(0, 0);
    
    if (notesHasContent(notas)) {
      builder.line(`  (Nota original: ${notas})`);
    }
  });
  
  builder.feed(1).align('center').bold(true).line(separator);
  builder.line('*** NO PREPARAR ***');
  builder.feed(4).cut();
  
  return builder.build();
};

/**
 * Genera el buffer de bytes ESC/POS para Comprobantes de Pago
 */
export const generarBytesComprobanteESCPOS = (datos, anchoPapel = 80) => {
  const builder = new EscPosBuilder();
  builder.init();
  
  const charCount = parseInt(anchoPapel, 10) === 58 ? 32 : 48;
  const separator = '='.repeat(charCount);
  const dashedSeparator = '-'.repeat(charCount);
  
  const rucText = datos.ruc || datos.empresaRuc || '';
  const empresaNombre = (datos.empresaNombre || datos.razonSocial || 'KARMA').toUpperCase();
  const subtituloEmpresa = (datos.subtituloEmpresa || 'Club and Lounge').toUpperCase();
  const direccionText = datos.direccion || datos.empresaDireccion || 'Av. Jose De San Martin 387';
  
  const est = String(datos.estado || '').toLowerCase();
  const esPagado = est === 'pagado' || est.endsWith('_pagado') || datos.esPagado === true;
  const tituloDoc = (datos.tituloDocumento || (esPagado ? 'COMPROBANTE DE PAGO' : 'CUENTA DE PEDIDOS')).toUpperCase();

  const pedidoNum = datos.pedidoId || datos.ordenId || datos.id || 'S/N';
  const mesaText = formatMesaHeader(datos.mesa, datos.paraLlevar);
  const pisoText = formatPisoHeader(datos.piso);
  const cajeroText = datos.cajero || datos.mozo || datos.usuario || 'Cajero';
  const cajaText = datos.caja || (datos.idCaja ? `CAJA #${datos.idCaja}` : 'CAJA PRINCIPAL');
  const fechaText = datos.fecha || new Date().toLocaleDateString('es-PE');
  const horaText = datos.hora || new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
  const metodoPago = (datos.metodoPago || datos.metodo_pago || 'EFECTIVO').toUpperCase();
  const totalPagar = parseFloat(datos.total || datos.totalPagar || 0).toFixed(2);

  // Encabezado Empresarial
  builder.align('center').bold(true).size(1, 1).line(empresaNombre);
  builder.size(0, 0).bold(true);
  if (subtituloEmpresa) builder.line(subtituloEmpresa);
  builder.bold(false);
  if (direccionText) builder.line(direccionText);
  if (rucText) builder.line(rucText);
  builder.feed(1);

  builder.bold(true).size(0, 0).line(tituloDoc);
  builder.bold(false);
  builder.line(separator);

  const lineTwoCols = (left, right) => {
    const spaceCount = charCount - left.length - right.length;
    if (spaceCount <= 0) {
      return left + ' ' + right;
    }
    return left + ' '.repeat(spaceCount) + right;
  };

  builder.align('left');
  builder.line(lineTwoCols(`TICKET: #${pedidoNum}`, `FECHA: ${fechaText}`));
  builder.line(lineTwoCols(`ORIGEN: ${mesaText}`, `HORA: ${horaText}`));
  if (pisoText) builder.line(`UBICACION: ${pisoText}`);
  builder.line(lineTwoCols(`CAJERO: ${cajeroText}`, `CAJA: ${cajaText}`));
  builder.line(dashedSeparator);

  // Cabecera Tabla Items
  if (charCount === 32) {
    builder.line('CANT DESCRIPCION      P.U   TOTAL');
  } else {
    builder.line('CANT  DESCRIPCION                 P.UNIT     TOTAL');
  }
  builder.line(dashedSeparator);

  const items = datos.items || datos.detalles || [];
  items.forEach(item => {
    const cant = String(item.cantidad || item.cant || 1);
    const nombre = (item.nombre || item.nombre_producto || item.producto || 'Producto').toUpperCase();
    const precio = parseFloat(item.precio || item.precio_unitario || 0).toFixed(2);
    const subtotal = parseFloat(item.subtotal || (item.cantidad * item.precio_unitario) || 0).toFixed(2);

    if (charCount === 32) {
      builder.bold(true).line(nombre);
      const colLeft = `  ${cant}x S/${precio}`;
      const colRight = `S/${subtotal}`;
      builder.bold(false).line(lineTwoCols(colLeft, colRight));
    } else {
      const cantFormatted = cant.padStart(3, ' ');
      const precioFormatted = `S/${precio}`.padStart(9, ' ');
      const subtotalFormatted = `S/${subtotal}`.padStart(10, ' ');
      const maxNombreLen = 22;
      const nombreTrunc = nombre.length > maxNombreLen ? nombre.substring(0, maxNombreLen) : nombre.padEnd(maxNombreLen, ' ');
      
      builder.line(`${cantFormatted} ${nombreTrunc} ${precioFormatted} ${subtotalFormatted}`);
    }
  });

  builder.line(separator);

  // Total destacado
  builder.align('right').bold(true).size(0, 0);
  if (esPagado) {
    builder.line(`TOTAL: S/ ${totalPagar}`);
    builder.feed(1);

    // Método de Pago & Pie
    builder.align('left').bold(true);
    if (metodoPago === 'VARIOS' && (Number(datos.pago_efectivo || 0) > 0 || Number(datos.pago_tarjeta || 0) > 0 || Number(datos.pago_yape || 0) > 0 || Number(datos.pago_plin || 0) > 0)) {
      builder.line('METODO DE PAGO: MIXTO');
      builder.bold(false);
      if (Number(datos.pago_efectivo || 0) > 0) builder.line(` - EFECTIVO: S/ ${Number(datos.pago_efectivo).toFixed(2)}`);
      if (Number(datos.pago_tarjeta || 0) > 0) builder.line(` - TARJETA:  S/ ${Number(datos.pago_tarjeta).toFixed(2)}`);
      if (Number(datos.pago_yape || 0) > 0)    builder.line(` - YAPE:     S/ ${Number(datos.pago_yape).toFixed(2)}`);
      if (Number(datos.pago_plin || 0) > 0)    builder.line(` - PLIN:     S/ ${Number(datos.pago_plin).toFixed(2)}`);
    } else {
      builder.line(`METODO DE PAGO: ${metodoPago}`);
    }
    builder.line(dashedSeparator);

    builder.align('center').bold(false);
    builder.line('¡GRACIAS POR SU PREFERENCIA!');
    builder.line('Vuelva pronto');
  } else {
    builder.line(`TOTAL PRE-CUENTA: S/. ${totalPagar}`);
    builder.feed(1);

    builder.align('center').bold(true);
    builder.line('*** CUENTA PENDIENTE DE PAGO ***');
  }
  builder.feed(4).cut();

  return builder.build();
};

/**
 * Formatea comanda o comprobante en bytes ESC/POS directos (Uint8Array) en el cliente
 */
export const generarBytesComandaESCPOS = (datos, tipoComanda, anchoPapel = 80) => {
  if (tipoComanda === 'COMPROBANTE') {
    return generarBytesComprobanteESCPOS(datos, anchoPapel);
  }
  if (tipoComanda === 'ANULACION' || tipoComanda === 'CANCELACION') {
    return generarBytesComandaAnulacionESCPOS(datos, anchoPapel);
  }
  return generarBytesComandaImpactoESCPOS(datos, anchoPapel);
};

/**
 * Genera el documento HTML formateado para Comandas Estándar / Nuevas
 */
export const generarHTMLComandaImpacto = (datos, anchoPapel = 80) => {
  const widthCss = parseInt(anchoPapel, 10) === 58 ? '58mm' : '80mm';
  const fontSizeBase = parseInt(anchoPapel, 10) === 58 ? '11px' : '13px';

  const mesaText = formatMesaHeader(datos.mesa, datos.paraLlevar);
  const pisoText = formatPisoHeader(datos.piso);
  const MozoText = datos.mozo || datos.usuario || 'Sistema';
  const fechaText = datos.fecha || new Date().toLocaleDateString('es-PE');
  const horaText = datos.hora || new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
  const numeroOrden = datos.ordenId || datos.id || datos.numeroComanda || 'S/N';

  const grupos = agruparItemsPorTiempo(datos.items || []);

  let htmlItems = '';
  Object.keys(grupos).forEach(nombreGrupo => {
    const list = grupos[nombreGrupo];
    if (list.length === 0) return;

    htmlItems += `
      <div style="margin-top: 10px; margin-bottom: 4px; border-bottom: 2px solid #000; padding-bottom: 2px;">
        <span style="font-size: 13px; font-weight: 900; background-color: #000; color: #fff; padding: 2px 6px; display: inline-block;">
          ▶ ${nombreGrupo}
        </span>
      </div>
    `;

    list.forEach(item => {
      const cant = item.cantidad || item.cant || 1;
      const nombre = item.nombre || item.producto || 'Producto';
      const notas = item.notas || item.observaciones || item.nota || '';

      htmlItems += `
        <div style="margin-bottom: 6px;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start;">
            <span style="font-size: 16px; font-weight: 900; min-width: 28px;">${cant}x</span>
            <span style="font-size: 14px; font-weight: 800; flex: 1; margin-left: 4px; text-transform: uppercase;">${nombre}</span>
          </div>
      `;

      if (notas) {
        htmlItems += `
          <div style="margin-left: 20px; margin-top: 2px; background-color: #f0f0f0; border-left: 3px solid #000; padding: 3px 6px; font-weight: bold;">
            <span style="font-size: 12px; display: block;">*** ATENCIÓN ***</span>
            <span style="font-size: 13px; text-transform: uppercase;">➔ ${notas}</span>
          </div>
        `;
      }

      htmlItems += `</div>`;
    });
  });

  const notaGeneral = datos.notaGeneral || datos.observacion;

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>Comanda - RealPOS</title>
        <style>
          @page { size: ${widthCss} auto; margin: 0; }
          body {
            width: ${widthCss};
            margin: 0 auto;
            padding: 6px 8px;
            font-family: 'Courier New', Courier, monospace, sans-serif;
            font-size: ${fontSizeBase};
            color: #000;
            line-height: 1.2;
          }
          .text-center { text-align: center; }
          .bold { font-weight: bold; }
          .header-box {
            border: 3px solid #000;
            padding: 6px;
            margin-bottom: 8px;
            text-align: center;
          }
          .header-title {
            font-size: 22px;
            font-weight: 900;
            letter-spacing: 1px;
            text-transform: uppercase;
            line-height: 1.1;
          }
          .sub-header {
            font-size: 14px;
            font-weight: bold;
            margin-top: 4px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            margin: 2px 0;
            border-bottom: 1px dashed #666;
            padding-bottom: 2px;
          }
          .divider-double {
            border-top: 2px solid #000;
            margin: 6px 0;
          }
        </style>
      </head>
      <body>
        <div class="header-box">
          <div class="header-title">${mesaText}</div>
          ${pisoText ? `<div class="sub-header">📍 ${pisoText}</div>` : ''}
        </div>

        <div class="info-row">
          <span>ORDEN: <strong>#${numeroOrden}</strong></span>
          <span>MOZO: <strong>${MozoText}</strong></span>
        </div>
        <div class="info-row">
          <span>FECHA: ${fechaText}</span>
          <span>HORA: ${horaText}</span>
        </div>

        ${htmlItems}

        ${notaGeneral ? `
          <div style="margin-top: 10px; border: 2px dashed #000; padding: 6px; text-align: center;">
            <div style="font-weight: 900; font-size: 13px;">*** ATENCIÓN: NOTA GENERAL ***</div>
            <div style="font-size: 13px; font-weight: bold; margin-top: 2px;">${notaGeneral}</div>
          </div>
        ` : ''}

        <div class="divider-double"></div>
        <div class="text-center bold" style="font-size: 11px; margin-top: 6px;">
          === FIN DE COMANDA ===
        </div>
        <div style="height: 35px;"></div>
      </body>
    </html>
  `;
};

/**
 * Genera el documento HTML formateado para Comandas de Anulación
 */
export const generarHTMLComandaAnulacion = (datos, anchoPapel = 80) => {
  const widthCss = parseInt(anchoPapel, 10) === 58 ? '58mm' : '80mm';
  const fontSizeBase = parseInt(anchoPapel, 10) === 58 ? '11px' : '13px';

  const mesaText = formatMesaHeader(datos.mesa, datos.paraLlevar);
  const pisoText = formatPisoHeader(datos.piso);
  const MozoText = datos.mozo || datos.usuario || 'Sistema';
  const horaText = datos.hora || new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
  const motivoAnulacion = datos.motivo || datos.razon || 'SOLICITUD DE CLIENTE';

  const items = datos.items || [];

  let htmlItems = '';
  items.forEach(item => {
    const cant = item.cantidad || item.cant || 1;
    const nombre = item.nombre || item.producto || 'Producto';
    const notas = item.notas || item.observaciones || '';

    htmlItems += `
      <div style="margin-bottom: 8px; border-bottom: 1px solid #000; padding-bottom: 4px;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
          <span style="font-size: 18px; font-weight: 900; color: #000;">❌ -${cant}x</span>
          <span style="font-size: 15px; font-weight: 900; flex: 1; margin-left: 6px; text-decoration: line-through; text-transform: uppercase;">${nombre}</span>
        </div>
        ${notas ? `<div style="font-size: 12px; font-weight: bold; margin-left: 24px;">(Nota original: ${notas})</div>` : ''}
      </div>
    `;
  });

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>Anulación - RealPOS</title>
        <style>
          @page { size: ${widthCss} auto; margin: 0; }
          body {
            width: ${widthCss};
            margin: 0 auto;
            padding: 6px 8px;
            font-family: 'Courier New', Courier, monospace, sans-serif;
            font-size: ${fontSizeBase};
            color: #000;
            line-height: 1.2;
          }
          .text-center { text-align: center; }
          .bold { font-weight: bold; }
          .banner-anulacion {
            background-color: #000;
            color: #fff;
            padding: 8px 4px;
            text-align: center;
            font-size: 16px;
            font-weight: 900;
            margin-bottom: 8px;
          }
          .header-box {
            border: 2px solid #000;
            padding: 4px;
            margin-bottom: 8px;
            text-align: center;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            margin: 3px 0;
          }
        </style>
      </head>
      <body>
        <div class="banner-anulacion">
          *** ⚠️ CANCELACIÓN / ANULACIÓN DE PLATO ⚠️ ***
        </div>

        <div class="header-box">
          <div style="font-size: 20px; font-weight: 900;">${mesaText}</div>
          ${pisoText ? `<div style="font-size: 13px; font-weight: bold;">📍 ${pisoText}</div>` : ''}
        </div>

        <div class="info-row">
          <span>HORA ANULACIÓN: <strong>${horaText}</strong></span>
          <span>MOZO: <strong>${MozoText}</strong></span>
        </div>

        <div style="margin: 8px 0; padding: 4px; border: 1px dashed #000; background-color: #f9f9f9;">
          <strong style="font-size: 11px;">MOTIVO DE ANULACIÓN:</strong>
          <div style="font-size: 12px; font-weight: bold; text-transform: uppercase;">${motivoAnulacion}</div>
        </div>

        <div style="margin-top: 10px; font-weight: 900; font-size: 13px; border-bottom: 2px solid #000;">
          ITEMS CANCELADOS:
        </div>

        ${htmlItems}

        <div style="border-top: 2px solid #000; margin-top: 10px; padding-top: 6px; text-align: center; font-size: 11px; font-weight: bold;">
          *** NO PREPARAR / DESCONTAR DE COMANDA ***
        </div>
        <div style="height: 35px;"></div>
      </body>
    </html>
  `;
};

/**
 * Genera el documento HTML formateado para Comprobantes de Pago
 */
export const generarHTMLComprobante = (datos, anchoPapel = 80) => {
  const widthCss = parseInt(anchoPapel, 10) === 58 ? '58mm' : '80mm';
  const fontSizeBase = parseInt(anchoPapel, 10) === 58 ? '11px' : '12px';

  const rucText = datos.ruc || datos.empresaRuc || '';
  const empresaNombre = datos.empresaNombre || datos.razonSocial || 'KARMA';
  const subtituloEmpresa = datos.subtituloEmpresa || 'Club and Lounge';
  const direccionText = datos.direccion || datos.empresaDireccion || 'Av. Jose De San Martin 387';

  const est = String(datos.estado || '').toLowerCase();
  const esPagado = est === 'pagado' || est.endsWith('_pagado') || datos.esPagado === true;
  const tituloDoc = datos.tituloDocumento || (esPagado ? 'COMPROBANTE DE PAGO' : 'CUENTA DE PEDIDOS');

  const pedidoNum = datos.pedidoId || datos.ordenId || datos.id || 'S/N';
  const mesaText = formatMesaHeader(datos.mesa, datos.paraLlevar);
  const pisoText = formatPisoHeader(datos.piso);
  const cajeroText = datos.cajero || datos.mozo || datos.usuario || 'Cajero';
  const cajaText = datos.caja || (datos.idCaja ? `Caja #${datos.idCaja}` : 'Caja Principal');
  const fechaText = datos.fecha || new Date().toLocaleDateString('es-PE');
  const horaText = datos.hora || new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
  const metodoPago = (datos.metodoPago || datos.metodo_pago || 'EFECTIVO').toUpperCase();
  const totalPagar = parseFloat(datos.total || datos.totalPagar || 0).toFixed(2);

  const items = datos.items || datos.detalles || [];

  let htmlItems = '';
  items.forEach(item => {
    const cant = item.cantidad || item.cant || 1;
    const nombre = item.nombre || item.nombre_producto || item.producto || 'Producto';
    const precio = parseFloat(item.precio || item.precio_unitario || 0).toFixed(2);
    const subtotal = parseFloat(item.subtotal || (cant * precio) || 0).toFixed(2);

    htmlItems += `
      <tr>
        <td style="text-align: center; vertical-align: top;">${cant}</td>
        <td style="text-align: left; vertical-align: top; font-weight: 600;">${nombre}</td>
        <td style="text-align: right; vertical-align: top;">S/ ${precio}</td>
        <td style="text-align: right; vertical-align: top; font-weight: bold;">S/ ${subtotal}</td>
      </tr>
    `;
  });

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>${tituloDoc} - RealPOS</title>
        <style>
          @page { size: ${widthCss} auto; margin: 0; }
          body {
            width: ${widthCss};
            margin: 0 auto;
            padding: 8px 10px;
            font-family: 'Courier New', Courier, monospace, sans-serif;
            font-size: ${fontSizeBase};
            color: #000;
            line-height: 1.3;
          }
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .bold { font-weight: bold; }
          .header-box {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 6px;
            margin-bottom: 6px;
          }
          .empresa-nombre {
            font-size: 22px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .ticket-titulo {
            font-size: 11px;
            font-weight: bold;
            margin-top: 4px;
            padding: 2px 0;
          }
          .info-grid {
            font-size: 11px;
            border-bottom: 1px dashed #000;
            padding-bottom: 6px;
            margin-bottom: 6px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin: 2px 0;
          }
          table.tabla-comprobante {
            width: 100%;
            border-collapse: collapse;
            margin-top: 6px;
            margin-bottom: 6px;
            font-size: 11px;
          }
          table.tabla-comprobante th {
            border-bottom: 1px solid #000;
            border-top: 1px solid #000;
            padding: 3px 2px;
            font-weight: bold;
          }
          table.tabla-comprobante td {
            padding: 3px 2px;
          }
          .total-box {
            border-top: 2px solid #000;
            border-bottom: 2px solid #000;
            padding: 6px 0;
            margin: 6px 0;
            text-align: right;
            font-size: 13px;
            font-weight: 900;
          }
          .metodo-pago-row {
            font-size: 12px;
            font-weight: bold;
            margin-bottom: 6px;
            border-bottom: 1px dashed #000;
            padding-bottom: 4px;
          }
          .footer {
            text-align: center;
            font-size: 11px;
            margin-top: 8px;
          }
        </style>
      </head>
      <body>
        <div class="header-box">
          <div class="empresa-nombre">${empresaNombre}</div>
          ${subtituloEmpresa ? `<div style="font-size: 13px; font-weight: bold;">${subtituloEmpresa}</div>` : ''}
          ${direccionText ? `<div style="font-size: 11px;">${direccionText}</div>` : ''}
          ${rucText ? `<div style="font-size: 11px; font-weight: bold;">${rucText}</div>` : ''}
          <div class="ticket-titulo">${tituloDoc}</div>
        </div>

        <div class="info-grid">
          <div class="info-row">
            <span>TICKET: <strong>#${pedidoNum}</strong></span>
            <span>FECHA: ${fechaText}</span>
          </div>
          <div class="info-row">
            <span>ORIGEN: <strong>${mesaText}</strong></span>
            <span>HORA: ${horaText}</span>
          </div>
          ${pisoText ? `<div class="info-row"><span>UBICACIÓN: ${pisoText}</span></div>` : ''}
          <div class="info-row">
            <span>CAJERO: ${cajeroText}</span>
            <span>CAJA: ${cajaText}</span>
          </div>
        </div>

        <table class="tabla-comprobante">
          <thead>
            <tr>
              <th style="width: 12%; text-align: center;">CANT</th>
              <th style="width: 48%; text-align: left;">DESCRIPCIÓN</th>
              <th style="width: 20%; text-align: right;">P.U</th>
              <th style="width: 20%; text-align: right;">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            ${htmlItems}
          </tbody>
        </table>

        ${esPagado ? `
          <div class="total-box">
            TOTAL: S/ ${totalPagar}
          </div>

          <div class="metodo-pago-row">
            ${metodoPago === 'VARIOS' && (Number(datos.pago_efectivo || 0) > 0 || Number(datos.pago_tarjeta || 0) > 0 || Number(datos.pago_yape || 0) > 0 || Number(datos.pago_plin || 0) > 0) ? `
              <div><strong>MÉTODO DE PAGO: MIXTO</strong></div>
              <div style="font-size: 10px; margin-left: 10px; text-align: left; font-weight: normal; margin-top: 4px;">
                ${Number(datos.pago_efectivo || 0) > 0 ? `<div>💵 Efectivo: S/ ${Number(datos.pago_efectivo).toFixed(2)}</div>` : ''}
                ${Number(datos.pago_tarjeta || 0) > 0 ? `<div>💳 Tarjeta:  S/ ${Number(datos.pago_tarjeta).toFixed(2)}</div>` : ''}
                ${Number(datos.pago_yape || 0) > 0 ? `<div>📱 Yape:     S/ ${Number(datos.pago_yape).toFixed(2)}</div>` : ''}
                ${Number(datos.pago_plin || 0) > 0 ? `<div>🌀 Plin:     S/ ${Number(datos.pago_plin).toFixed(2)}</div>` : ''}
              </div>
            ` : `
              MÉTODO DE PAGO: ${metodoPago}
            `}
          </div>

          <div class="footer">
            <div class="bold">¡GRACIAS POR SU PREFERENCIA!</div>
            <div>Vuelva pronto</div>
          </div>
        ` : `
          <div class="total-box">
            TOTAL PRE-CUENTA: S/. ${totalPagar}
          </div>

          <div class="footer" style="font-weight: bold; font-size: 13px;">
            *** CUENTA PENDIENTE DE PAGO ***
          </div>
        `}
        <div style="height: 35px;"></div>
      </body>
    </html>
  `;
};

// ─── EJECUCIÓN FÍSICA DE IMPRESIÓN ─────────────────────────────────────────

/**
 * Imprime el documento HTML ejecutando envío directo en background usando iframe oculto
 */
const imprimirViaIframeOculto = (htmlContent) => {
  return new Promise((resolve, reject) => {
    try {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(htmlContent);
      doc.close();

      iframe.contentWindow.focus();

      setTimeout(() => {
        try {
          iframe.contentWindow.print();
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
            resolve({ success: true, method: 'iframe_background' });
          }, 800);
        } catch (printErr) {
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
          reject(printErr);
        }
      }, 350);
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Impresión via Web Bluetooth API en background si está configurada
 */
export const imprimirViaBluetooth = async (datosComanda, tipoComanda, impresora, permitirPicker = false) => {
  // 0. Si se está ejecutando dentro de nuestra App Nativa Android, usar el puente Bluetooth directo por RFCOMM
  if (typeof window !== 'undefined' && window.AndroidPrinter && typeof window.AndroidPrinter.printBluetooth === 'function') {
    try {
      const deviceName = impresora?.nombre || 'BlueTooth Printer';
      const piso = datosComanda.piso !== undefined ? datosComanda.piso : (impresora?.piso || 1);
      const area = datosComanda.area || impresora?.area || 'todas';
      const anchoPapel = datosComanda.anchoPapel || impresora?.ancho_papel || getPaperFormatForArea(piso, area);

      const binaryBuffer = (tipoComanda === 'COMPROBANTE')
        ? generarBytesComprobanteESCPOS(datosComanda, anchoPapel)
        : generarBytesComandaESCPOS(datosComanda, tipoComanda, anchoPapel);

      const base64Data = arrayBufferToBase64(binaryBuffer);
      console.log(`📱 [App Nativa BT] Enviando comanda directa vía Bluetooth a "${deviceName}"...`);
      
      const res = window.AndroidPrinter.printBluetooth(deviceName, base64Data);
      if (res === 'SUCCESS') {
        return { success: true, method: 'android_native_bluetooth', deviceName: deviceName };
      }
      throw new Error(res);
    } catch (nativeErr) {
      console.error('⚠️ Fallo en puente nativo AndroidPrinter Bluetooth:', nativeErr);
      throw new Error(`Fallo en conexión nativa Bluetooth: ${nativeErr.message}`);
    }
  }

  if (!('bluetooth' in navigator)) {
    throw new Error('Web Bluetooth API no está soportada en este navegador');
  }

  const targetServices = [
    '000018f0-0000-1000-8000-0000509589c9',
    '00001101-0000-1000-8000-0000509589c9',
    '0000e7e1-0000-1000-8000-0000509589c9',
    '0000ff00-0000-1000-8000-0000509589c9',
    '49535343-fe7d-4ae5-8fa9-9fafd205e455'
  ];

  let device = await restoreBluetoothDevice(impresora);

  if (!device) {
    // Si no hay dispositivo en caché ni en getDevices(), abrir el picker automáticamente.
    // Esto permite reconectar sin perder la impresión pendiente, incluso en recargas.
    console.log('📶 Dispositivo Bluetooth no encontrado en caché. Abriendo picker para reconexión automática...');
    try {
      device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: targetServices
      });
      if (device) {
        // Persistir en RAM y localStorage para las próximas impresiones
        cachedBluetoothDevice = device;
        setCachedBluetoothDevice(device);
      }
    } catch (pickerErr) {
      if (pickerErr.name === 'NotFoundError' || pickerErr.message?.includes('cancelled')) {
        throw new Error('Vinculación cancelada por el usuario. Por favor selecciona tu ticketera Bluetooth para imprimir.');
      }
      throw new Error(`No se pudo reconectar la impresora Bluetooth: ${pickerErr.message}`);
    }
  }

  if (!device) {
    throw new Error('Dispositivo Bluetooth no seleccionado');
  }

  console.log(`🔌 Conectando al dispositivo Bluetooth: ${device.name || 'Desconocido'}...`);
  let server = null;
  try {
    server = await device.gatt.connect();
    
    let characteristic = null;
    
    // Buscar la característica de escritura en los servicios configurados
    for (const serviceUuid of targetServices) {
      try {
        const service = await server.getPrimaryService(serviceUuid);
        const characteristics = await service.getCharacteristics();
        for (const char of characteristics) {
          if (char.properties.write || char.properties.writeWithoutResponse) {
            characteristic = char;
            break;
          }
        }
      } catch (e) {
        // Ignorar error de servicio no soportado y seguir probando
      }
      if (characteristic) break;
    }

    // Fallback general: intentar listar todos los servicios primarios permitidos
    if (!characteristic) {
      try {
        const services = await server.getPrimaryServices();
        for (const service of services) {
          const characteristics = await service.getCharacteristics();
          for (const char of characteristics) {
            if (char.properties.write || char.properties.writeWithoutResponse) {
              characteristic = char;
              break;
            }
          }
          if (characteristic) break;
        }
      } catch (e) {
        console.warn('Error al buscar características en todos los servicios:', e);
      }
    }

    if (!characteristic) {
      throw new Error('No se encontró una característica de escritura en el dispositivo Bluetooth');
    }

    // Formatear la comanda en bytes ESC/POS directos
    const piso = datosComanda.piso !== undefined ? datosComanda.piso : (impresora?.piso || 1);
    const area = datosComanda.area || impresora?.area || 'todas';
    const anchoPapel = datosComanda.anchoPapel || impresora?.ancho_papel || getPaperFormatForArea(piso, area);
    
    const binaryBuffer = generarBytesComandaESCPOS(datosComanda, tipoComanda, anchoPapel);
    console.log(`📦 Buffer ESC/POS generado: ${binaryBuffer.length} bytes. Enviando en fragmentos de 20 bytes...`);

    // Fragmentar en trozos de 20 bytes (límite MTU estándar)
    const mtu = 20;
    for (let i = 0; i < binaryBuffer.length; i += mtu) {
      const chunk = binaryBuffer.slice(i, i + mtu);
      if (typeof characteristic.writeValueWithoutResponse === 'function' && characteristic.properties.writeWithoutResponse) {
        await characteristic.writeValueWithoutResponse(chunk);
      } else if (typeof characteristic.writeValueWithResponse === 'function') {
        await characteristic.writeValueWithResponse(chunk);
      } else {
        await characteristic.writeValue(chunk);
      }
    }

    console.log('✅ Envío Bluetooth completado con éxito');
    return { success: true, method: 'bluetooth', deviceName: device.name };
  } finally {
    if (server && server.connected) {
      console.log('🔌 Desconectando del dispositivo Bluetooth...');
      server.disconnect();
    } else if (device && device.gatt && device.gatt.connected) {
      device.gatt.disconnect();
    }
  }
};

const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

/**
 * Impresión directa por Red IP en background
 */
const imprimirViaRedIP = async (datosComanda, tipoComanda, impresora, htmlContent) => {
  const ipAddress = impresora?.ip || impresora?.direccion_ip || '192.168.1.200';
  const port = impresora?.puerto || 9100;

  // Bloquear de inmediato el modo Red IP si estamos en el navegador web
  if (typeof window === 'undefined' || !window.AndroidPrinter) {
    throw new Error('El modo de impresión por Red IP Directa no está soportado en navegadores web. Por favor usa la APK nativa en la tablet.');
  }

  // 0. Si se está ejecutando dentro de nuestra App Nativa Android, usar el puente directo por TCP sockets
  if (window.AndroidPrinter && typeof window.AndroidPrinter.print === 'function') {
    try {
      const piso = datosComanda.piso !== undefined ? datosComanda.piso : (impresora?.piso || 1);
      const area = datosComanda.area || impresora?.area || 'todas';
      const anchoPapel = datosComanda.anchoPapel || impresora?.ancho_papel || getPaperFormatForArea(piso, area);

      const binaryBuffer = (tipoComanda === 'COMPROBANTE')
        ? generarBytesComprobanteESCPOS(datosComanda, anchoPapel)
        : generarBytesComandaESCPOS(datosComanda, tipoComanda, anchoPapel);

      const base64Data = arrayBufferToBase64(binaryBuffer);
      console.log(`📱 [App Nativa] Enviando comanda directa vía socket TCP a ${ipAddress}:${port}...`);
      
      const res = window.AndroidPrinter.print(ipAddress, parseInt(port, 10), base64Data);
      if (res === 'SUCCESS') {
        return { success: true, method: 'android_native_bridge', ip: ipAddress };
      }
      throw new Error(res);
    } catch (nativeErr) {
      console.error('⚠️ Fallo en puente nativo AndroidPrinter:', nativeErr);
      throw new Error(`Fallo en conexión nativa a impresora IP: ${nativeErr.message}`);
    }
  }

  // 1. Intentar primero impresión directa vía RawBT Local (Tablet/Celular Android)
  try {
    const piso = datosComanda.piso !== undefined ? datosComanda.piso : (impresora?.piso || 1);
    const area = datosComanda.area || impresora?.area || 'todas';
    const anchoPapel = datosComanda.anchoPapel || impresora?.ancho_papel || getPaperFormatForArea(piso, area);

    const binaryBuffer = (tipoComanda === 'COMPROBANTE')
      ? generarBytesComprobanteESCPOS(datosComanda, anchoPapel)
      : generarBytesComandaESCPOS(datosComanda, tipoComanda, anchoPapel);

    console.log(`📡 Intentando envío de impresión directa vía RawBT Local (http://localhost:40213)...`);
    const rawBtController = new AbortController();
    const rawBtTimeoutId = setTimeout(() => rawBtController.abort(), 2000);
    const rawBtRes = await fetch('http://localhost:40213/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: binaryBuffer,
      signal: rawBtController.signal
    });
    clearTimeout(rawBtTimeoutId);
    if (rawBtRes.ok) {
      console.log('✅ Impresión directa vía RawBT completada con éxito');
      return { success: true, method: 'rawbt_local_ip', ip: ipAddress };
    }
  } catch (rawBtErr) {
    console.warn('⚠️ RawBT Local no disponible. Continuando con canal de red convencional...', rawBtErr);
  }

  // 2. Fallback: Canal de red directa convencional (sujeto a restricciones CORS/Mixed Content del navegador)
  console.log(`📡 Enviando impresión directa en background a Red IP (${ipAddress}:${port})...`);
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    const res = await fetch(`http://${ipAddress}:${port}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/html' },
      body: htmlContent,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (res.ok) {
      return { success: true, method: 'red_ip', ip: ipAddress };
    }
    throw new Error(`Respuesta HTTP ${res.status} desde impresora IP ${ipAddress}:${port}`);
  } catch (e) {
    console.error(`Fallo en conexión directa a impresora Red IP ${ipAddress}:${port}:`, e);
    throw new Error(`Fallo en conexión IP ${ipAddress}:${port}: ${e.message}`);
  }
};

/**
 * Invoca window.print() de previsualización para permitir inspección previa del usuario antes de enviar
 */
const imprimirViaVentanaConfirmacion = (htmlContent) => {
  return new Promise((resolve, reject) => {
    try {
      const printWin = window.open('', '_blank', 'width=480,height=650,top=50,left=50,scrollbars=yes');
      if (printWin) {
        printWin.document.open();
        printWin.document.write(htmlContent);
        printWin.document.close();
        printWin.focus();
        setTimeout(() => {
          try {
            printWin.print();
            resolve({ success: true, method: 'window.print_preview' });
          } catch (err) {
            reject(err);
          }
        }, 350);
      } else {
        // En caso de que emergentes estén bloqueados, usamos iframe visible temporal para activar window.print() de previsualización
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.top = '10%';
        iframe.style.left = '10%';
        iframe.style.width = '80vw';
        iframe.style.height = '80vh';
        iframe.style.zIndex = '999999';
        iframe.style.boxShadow = '0 10px 30px rgba(0,0,0,0.5)';
        iframe.style.background = '#fff';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(htmlContent);
        doc.close();

        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          resolve({ success: true, method: 'window.print_preview_iframe' });
        }, 1000);
      }
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Ejecución interna de la impresión física.
 * Condiciona el modo de emisión:
 *  - Si el selector está en 'silencioso' (Kiosk Mode): ejecutar el envío directo en background (Web Bluetooth API / Red IP / Iframe oculto).
 *  - Si el selector está en 'confirmacion': invocar window.print() de previsualización para permitir inspección previa del usuario antes de enviar.
 */
const ejecutarImpresionFisica = async (datosComanda, tipoComanda, esReintento = false) => {
  const userId = datosComanda?.userId || getCurrentUserId();
  const localPrinter = getLocalPrinter(userId);
  const piso = datosComanda.piso !== undefined ? datosComanda.piso : (localPrinter?.piso || 1);
  const area = datosComanda.area || localPrinter?.area || 'todas';

  const anchoPapel = datosComanda.anchoPapel || localPrinter?.ancho_papel || getPaperFormatForArea(piso, area, userId);
  const tipoConexion = localPrinter?.tipo_conexion || 'virtual';

  if (tipoConexion === 'virtual' || tipoConexion === 'desactivado') {
    console.log('📱 Modo Virtual activo: Omite la impresión física en papel.');
    return { success: true, method: 'virtual_kds', message: 'Comanda procesada en Modo Virtual (Sin papel físico).' };
  }

  const htmlDoc = (tipoComanda === 'COMPROBANTE')
    ? generarHTMLComprobante(datosComanda, anchoPapel)
    : (tipoComanda === 'ANULACION' || tipoComanda === 'CANCELACION')
      ? generarHTMLComandaAnulacion(datosComanda, anchoPapel)
      : generarHTMLComandaImpacto(datosComanda, anchoPapel);

  const modoEmision = getSilentModeType(userId); // 'silencioso' vs 'confirmacion'

  if (modoEmision === 'confirmacion') {
    // Modo CONFIRMACIÓN: Invocar window.print() de previsualización para inspección previa del usuario antes de enviar
    console.log('📄 Modo Confirmación activo: Invocando window.print() de previsualización para inspección previa...');
    return await imprimirViaVentanaConfirmacion(htmlDoc);
  }

  // Modo SILENCIOSO (Kiosk Mode): Ejecutar el envío directo en background (Web Bluetooth API / Red IP / Iframe oculto)
  console.log('🚀 Modo Silencioso activo (Kiosk Mode): Ejecutando envío directo en background...');

  if (tipoConexion === 'bluetooth') {
    try {
      return await imprimirViaBluetooth(datosComanda, tipoComanda, localPrinter, false);
    } catch (btErr) {
      console.error('Error en impresión Bluetooth directa:', btErr);
      throw new Error(`Fallo en conexión Bluetooth: ${btErr.message}`);
    }
  } else if (tipoConexion === 'ip' || tipoConexion === 'red') {
    try {
      return await imprimirViaRedIP(datosComanda, tipoComanda, localPrinter, htmlDoc);
    } catch (ipErr) {
      console.error('Error en impresión Red IP directa:', ipErr);
      throw new Error(`Fallo en conexión Red IP: ${ipErr.message}`);
    }
  }

  return await imprimirViaIframeOculto(htmlDoc);
};

/**
 * Función principal expuesta para imprimir comandas en el cliente.
 * Si falla la impresión o no hay red, guarda automáticamente en la cola offline.
 *
 * @param {Object} datosComanda Objeto con la información de la comanda (mesa, piso, mozo, items, etc.)
 * @param {String} tipoComanda 'NUEVA' | 'ANULACION' | 'CANCELACION' | 'COMPROBANTE'
 */
export const imprimirComandaCliente = async (datosComanda, tipoComanda = 'NUEVA') => {
  try {
    const resultado = await ejecutarImpresionFisica(datosComanda, tipoComanda);
    return {
      success: true,
      message: 'Comanda procesada correctamente por el motor de impresión',
      ...resultado
    };
  } catch (error) {
    console.error('⚠️ Fallo en la emisión de comanda:', error);
    const queuedJob = addToPrintQueue(datosComanda, tipoComanda);
    return {
      success: false,
      queued: true,
      jobId: queuedJob?.id,
      message: 'No se pudo conectar con la impresora física. Comanda guardada en la cola offline para reintento automático.',
      error: error.message
    };
  }
};

/**
 * Función expuesta para imprimir comanda de anulación en el cliente.
 * @param {Object} datosAnulacion Objeto con la información de la anulación (mesa, piso, mozo, items anulados, motivo, etc.)
 */
export const imprimirAnulacionCliente = async (datosAnulacion) => {
  return await imprimirComandaCliente(datosAnulacion, 'ANULACION');
};

/**
 * Función expuesta para imprimir comprobante de pago en el cliente.
 * @param {Object} datosComprobante Objeto con la información del comprobante de pago
 */
export const imprimirComprobanteCliente = async (datosComprobante) => {
  return await imprimirComandaCliente(datosComprobante, 'COMPROBANTE');
};

const clientPrinterService = {
  getCurrentUserId,
  getLocalPrinterKey,
  getSilentPrintKey,
  getSilentMode,
  getSilentModeType,
  setSilentMode,
  getPaperFormats,
  saveAllPaperFormats,
  setPaperFormat,
  getPaperFormatForArea,
  getLocalPrinter,
  setLocalPrinter,
  clearLocalPrinter,
  getCachedBluetoothDevice,
  setCachedBluetoothDevice,
  restoreBluetoothDevice,
  getPrintQueue,
  addToPrintQueue,
  removeFromPrintQueue,
  processPrintQueue,
  generarHTMLComandaImpacto,
  generarHTMLComandaAnulacion,
  generarHTMLComprobante,
  generarBytesComandaImpactoESCPOS,
  generarBytesComandaAnulacionESCPOS,
  generarBytesComprobanteESCPOS,
  generarBytesComandaESCPOS,
  imprimirViaBluetooth,
  imprimirComandaCliente,
  imprimirAnulacionCliente,
  imprimirComprobanteCliente
};

export default clientPrinterService;

